/**
 * Token Manager
 * Handles refresh token and access token storage and retrieval
 */

const TOKEN_KEYS = {
  REFRESH_TOKEN: 'refreshToken',
  ACCESS_TOKEN: 'accessToken',
  REDIRECT_URL: 'redirectUrl'  // VIO redirect URL for voucher navigation
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = () => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Save refresh token to localStorage
 */
export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
    console.log('Refresh token saved to localStorage');
  }
};

/**
 * Get access token from localStorage
 */
export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * Save access token to localStorage
 */
export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
    console.log('Access token saved to localStorage');
  }
};

/**
 * Clear all tokens from localStorage
 */
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REDIRECT_URL);
  console.log('All tokens cleared from localStorage');
};

/**
 * Check if refresh token exists in URL and save it
 * Returns the refresh token if found
 */
export const checkAndSaveRefreshTokenFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const refreshToken = urlParams.get('refreshToken');
  
  if (refreshToken) {
    // Decode the token (it's URL encoded)
    const decodedToken = decodeURIComponent(refreshToken);
    setRefreshToken(decodedToken);
    console.log('Refresh token found in URL and saved');
    
    // Clean up URL (remove token from URL for security)
    const url = new URL(window.location.href);
    url.searchParams.delete('refreshToken');
    window.history.replaceState({}, document.title, url.pathname + url.search);
    
    return decodedToken;
  }
  
  return null;
};

/**
 * Get redirect URL from localStorage
 * This URL is used for navigating to VIO member PWA (voucher detail, etc.)
 * Example claim/detail (template): {redirectUrl}/vouchers/{voucherId} (GET /api/vouchers/:voucherId)
 */
export const getRedirectUrl = () => {
  return localStorage.getItem(TOKEN_KEYS.REDIRECT_URL);
};

/**
 * Save redirect URL to localStorage
 * This URL will be used for voucher navigation to VIO pages
 */
export const setRedirectUrl = (url) => {
  if (url) {
    localStorage.setItem(TOKEN_KEYS.REDIRECT_URL, url);
    console.log('Redirect URL saved to localStorage:', url);
  }
};

/**
 * Check if redirect URL exists in URL parameters and save it
 * Example: redirectUrl=https://example.com/tenant-slug
 * Used as member PWA origin/path for links such as .../voucher/{userVoucherId}
 * Returns the redirect URL if found
 */
export const checkAndSaveRedirectUrlFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUrl = urlParams.get('redirectUrl');
  
  if (redirectUrl) {
    // Decode the URL (it's URL encoded)
    const decodedUrl = decodeURIComponent(redirectUrl);
    setRedirectUrl(decodedUrl);
    console.log('Redirect URL found in URL and saved');
    
    // Clean up URL (remove redirectUrl from URL)
    const url = new URL(window.location.href);
    url.searchParams.delete('redirectUrl');
    window.history.replaceState({}, document.title, url.pathname + url.search);
    
    return decodedUrl;
  }
  
  return null;
};

/**
 * Get company from localStorage
 */
export const getCompany = () => {
  return localStorage.getItem(USER_INFO_KEYS.COMPANY);
};

/**
 * Save company to localStorage
 */
export const setCompany = (company) => {
  if (company) {
    localStorage.setItem(USER_INFO_KEYS.COMPANY, company);
    console.log('Company saved to localStorage:', company);
  }
};

/**
 * Check if company exists in URL parameters and save it
 * Example: company=ABC%20Corp
 * This company info is provided by VIO miniapp URL
 * Returns the company if found
 */
export const checkAndSaveCompanyFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const company = urlParams.get('company');
  
  if (company) {
    // Decode the company (it's URL encoded)
    const decodedCompany = decodeURIComponent(company);
    setCompany(decodedCompany);
    console.log('Company found in URL and saved');
    
    // Clean up URL (remove company from URL)
    const url = new URL(window.location.href);
    url.searchParams.delete('company');
    window.history.replaceState({}, document.title, url.pathname + url.search);
    
    return decodedCompany;
  }
  
  return null;
};

/**
 * Check if user has valid tokens
 */
export const hasValidTokens = () => {
  return !!(getRefreshToken() && getAccessToken());
};

/**
 * User Info Management
 */
const USER_INFO_KEYS = {
  USERNAME: 'username',
  EMAIL: 'email',
  PHONE_NUMBER: 'phoneNumber',
  USER_ID: 'userId',
  COMPANY: 'company',
  WALLET_ADDR: 'walletAddr'
};

/**
 * Save user info to localStorage
 * Replaces all user info fields
 * Note: Company is now from URL parameter, not from user/info API
 */
export const setUserInfo = (userInfo) => {
  if (!userInfo) return;
  
  // Clear old user info first (except company, which comes from URL)
  const keysToRemove = Object.values(USER_INFO_KEYS).filter(key => key !== USER_INFO_KEYS.COMPANY);
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Save new user info
  if (userInfo.username) {
    localStorage.setItem(USER_INFO_KEYS.USERNAME, userInfo.username);
  }
  if (userInfo.email) {
    localStorage.setItem(USER_INFO_KEYS.EMAIL, userInfo.email);
  }
  if (userInfo.phoneNumber) {
    localStorage.setItem(USER_INFO_KEYS.PHONE_NUMBER, userInfo.phoneNumber);
  }
  if (userInfo.userId) {
    localStorage.setItem(USER_INFO_KEYS.USER_ID, userInfo.userId);
  }
  if (userInfo.walletAddr) {
    localStorage.setItem(USER_INFO_KEYS.WALLET_ADDR, userInfo.walletAddr);
  }
  // Company is NOT saved here - it comes from URL parameter
  
  console.log('User info saved to localStorage');
};

/**
 * Get user info from localStorage
 */
export const getUserInfo = () => {
  return {
    username: localStorage.getItem(USER_INFO_KEYS.USERNAME),
    email: localStorage.getItem(USER_INFO_KEYS.EMAIL),
    phoneNumber: localStorage.getItem(USER_INFO_KEYS.PHONE_NUMBER),
    userId: localStorage.getItem(USER_INFO_KEYS.USER_ID),
    company: localStorage.getItem(USER_INFO_KEYS.COMPANY),
    walletAddr: localStorage.getItem(USER_INFO_KEYS.WALLET_ADDR)
  };
};

/**
 * Clear user info from localStorage
 */
export const clearUserInfo = () => {
  Object.values(USER_INFO_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  console.log('User info cleared from localStorage');
};

/**
 * Get username
 */
export const getUsername = () => {
  return localStorage.getItem(USER_INFO_KEYS.USERNAME);
};

/**
 * Get user ID
 */
export const getUserId = () => {
  return localStorage.getItem(USER_INFO_KEYS.USER_ID);
};

